import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  ConfirmationService,
  MenuItem,
  MessageService,
} from 'primeng/api';
import { LayoutService } from './service/app.layout.service';
import { AuthService } from '../modules/auth/services/auth.service';
import { UserService } from '../../core/services/user.service';
import { SETTINGS } from '../../core/configs/common.settings';
import { Router } from '@angular/router';
import { CompanyService } from '../../core/services/company.service';
import { getItem, setItem } from '../../core/utils/safe-storage';
import { PermissionService } from '../../core/services/permission.service';
import { PERMISSION } from '../../core/configs/permissions.config';

@Component({
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
})
export class AppTopBarComponent implements OnInit {
  items!: MenuItem[];
  dropdownItems: MenuItem[];
  mobileDropdownItems: MenuItem[];
  loggedInUser: any;
  companies: MenuItem[] = [];
  /** Items for the Active Company pill's switcher menu. */
  companyMenuItems: MenuItem[] = [];
  companyData: any[] = [];
  /** Permission-based list of companies the user can access (from API). */
  accessibleCompanies: any[] = [];
  companyName: any = '';
  userLoading = false;

  @ViewChild('menubutton') menuButton!: ElementRef;

  @ViewChild('topbarmenubutton') topbarMenuButton!: ElementRef;

  @ViewChild('topbarmenu') menu!: ElementRef;

  constructor(
    public layoutService: LayoutService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private companyService: CompanyService,
    public permissionService: PermissionService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.dropdownItems = [];
    this.mobileDropdownItems = [];
  }

  ngOnInit() {
    this.companyName = getItem(SETTINGS.COMPANY_NAME);
    this.getLoggedInUserDetails();
    this.loadCompaniesAndSetupMenu();
    this.permissionService.loadEffectiveFromServer();
  }

  setupDropdownMenu() {
    if (!this.loggedInUser) return;
    this.dropdownItems = [
      {
        label: 'Profile',
        command: () => {
          this.router.navigate([`/app/user/profile/${this.loggedInUser._id}`]);
        },
      }
    ];

    // Job Billing stays in profile menu; Invoice Generator lives under Invoices (IMP-70).
    if (this.permissionService.hasPermission(PERMISSION.BILLING_VIEW)) {
      this.dropdownItems.push({
        label: 'Job Billing',
        command: () => {
          this.router.navigate(['/app/job-billing']);
        },
      });
    }

    const settingsItems: MenuItem[] = [
      {
        label: 'Holiday Management',
        visible: this.permissionService.hasPermission(PERMISSION.HOLIDAY_MANAGE),
        command: () => {
          this.router.navigate(['/app/holiday']);
        },
      },
      {
        label: 'Permissions',
        visible: this.permissionService.hasPermission(PERMISSION.PERMISSION_MANAGE),
        command: () => {
          this.router.navigate(['/app/user/permissions']);
        },
      },
      {
        label: 'General',
        visible: this.permissionService.hasPermission(PERMISSION.PERMISSION_MANAGE),
        command: () => {
          this.router.navigate(['/app/user/revamp-settings']);
        },
      },
    ].filter((item) => item.visible !== false);

    this.dropdownItems.push(
      {
        label: 'Company',
        items: this.companies,
      },
      {
        label: 'Settings',
        visible: settingsItems.length > 0,
        items: settingsItems,
      },
      {
        label: 'Logout',
        command: () => {
          this.authService.logout();
        },
        styleClass: 'logout-link',
      }
    );
  }

  getLoggedInUserDetails(): void {
    this.userLoading = true;
    this.userService
      .userDetailsById(getItem(SETTINGS.LOGGED_IN_USER_ID))
      .subscribe(
        (res: any) => {
          this.loggedInUser = res as any;
          this.authService.syncRoleFromUser(this.loggedInUser);
          this.userLoading = false;
          this.setupDropdownMenu(); // Set up dropdown menu after user data is loaded
        },
        (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `${
              error?.error?.message ? error?.error?.message : 'Error'
            }`,
          });
          this.userLoading = false;
        }
      );
  }

  getCompanies(): void {
    this.loadCompaniesAndSetupMenu();
  }

  /**
   * Load permission-based companies from API, then build company menu and apply single-company auto-select.
   */
  loadCompaniesAndSetupMenu(): void {
    this.companyService.getAllCompanies().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : [];
        this.accessibleCompanies = list.map((c: any) => ({
          _id: c._id,
          name: c.name || c.email || '',
        }));
        this.companyData = this.accessibleCompanies;

        if (this.accessibleCompanies.length === 1) {
          const single = this.accessibleCompanies[0];
          setItem(SETTINGS.COMPANY, single._id);
          setItem(SETTINGS.COMPANY_NAME, single.name);
          this.companyName = single.name;
          this.permissionService.loadEffectiveFromServer(single._id);
        }

        this.buildCompanyMenuItems();
        this.setupDropdownMenu();
      },
      error: () => {
        this.accessibleCompanies = [];
        this.buildCompanyMenuItems();
        this.setupDropdownMenu();
      },
    });
  }

  /** Build menu items for company dropdown: only permitted companies; add "All" only if user has 2+ companies. */
  private buildCompanyMenuItems(): void {
    this.companies = [];
    this.accessibleCompanies.forEach((com: any) => {
      this.companies.push({
        label: com.name,
        command: () => this.setCompanyForFilter(com),
      });
    });
    if (this.accessibleCompanies.length >= 2) {
      this.companies.push({
        label: 'All',
        command: () => this.setCompanyToAll(),
      });
    }
    this.mobileDropdownItems = [...this.companies];
    this.buildCompanySwitcherItems();
  }

  get canSwitchCompany(): boolean {
    return this.accessibleCompanies.length >= 2;
  }

  private buildCompanySwitcherItems(): void {
    if (!this.canSwitchCompany) {
      this.companyMenuItems = [];
      return;
    }
    const selectedId = getItem(SETTINGS.COMPANY) || '';
    const option = (label: string, active: boolean, select: () => void): MenuItem => ({
      label,
      icon: active ? 'pi pi-check' : 'pi pi-fw',
      styleClass: active ? 'topbar-company-menu__active' : undefined,
      command: () => {
        if (!active) select();
      },
    });
    this.companyMenuItems = [
      {
        label: 'Switch company',
        disabled: true,
        styleClass: 'topbar-company-menu__header',
      },
      ...this.accessibleCompanies.map((com: any) =>
        option(com.name, com._id === selectedId, () => this.setCompanyForFilter(com))
      ),
      { separator: true },
      option('All Companies', !selectedId, () => this.setCompanyToAll()),
    ];
  }

  setCompanyToAll(): void {
    this.confirmationService.confirm({
      key: 'companyFilter',
      message: `Are you sure you want to change the company filter to all?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setItem(SETTINGS.COMPANY, '');
        setItem(SETTINGS.COMPANY_NAME, '');
        this.companyName = this.getActiveCompanyDisplayLabel();
        location.reload();
      },
      reject: () => {},
    });
  }

  /**
   * Display label for Active Company on top bar by permission and selection:
   * - 1 company: always that company name
   * - 2+ companies with "All" selected: "All Companies" (means all companies the user has access to)
   * - Specific company selected: that company name
   */
  getActiveCompanyDisplayLabel(): string {
    const selectedId = getItem(SETTINGS.COMPANY);
    const selectedName = getItem(SETTINGS.COMPANY_NAME);
    const count = this.accessibleCompanies.length;

    if (selectedId && selectedName) return selectedName;
    if (count === 0) return 'All Companies';
    if (count === 1) return this.accessibleCompanies[0]?.name || 'All Companies';
    // User has 2+ companies and "All" is selected → show "All Companies"
    return 'All Companies';
  }

  setCompanyForFilter(company: any): void {
    this.confirmationService.confirm({
      key: 'companyFilter',
      message: `Are you sure you want to switch the company filter to '${company.name}'?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setItem(SETTINGS.COMPANY, company._id);
        setItem(SETTINGS.COMPANY_NAME, company.name);
        this.companyName = company.name;
        location.reload();
      },
      reject: () => {},
    });
  }

  changeTheme(theme: string, colorScheme: string) {
    const themeLink = document.getElementById('theme-css') as HTMLLinkElement | null;
    if (!themeLink?.href) return;
    const newHref = themeLink.getAttribute('href')?.replace(this.layoutService.config.theme, theme);
    if (!newHref) return;
    this.replaceThemeLink(newHref, () => {
      this.layoutService.config.theme = theme;
      this.layoutService.config.colorScheme = colorScheme;
      this.layoutService.onConfigUpdate();
    });
  }

  replaceThemeLink(href: string, onComplete: () => void) {
    const id = 'theme-css';
    const themeLink = document.getElementById('theme-css') as HTMLLinkElement | null;
    if (!themeLink?.parentNode) return;
    const cloneLinkElement = themeLink.cloneNode(true) as HTMLLinkElement;
    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');
    themeLink.parentNode.insertBefore(cloneLinkElement, themeLink.nextSibling);
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
      onComplete();
    });
  }

  logout(): void {
    this.authService.logout();
  }
}
